// ============================================================================
// EcgStudyDetailDialog.tsx — КАРТОЧКА ИССЛЕДОВАНИЯ ЭКГ (экран врача).
//
// Данные читаются заново по id (SSoT — сервер): снимок/файл, измерения,
// вероятностные находки, качество, пациент. Заключение врача вводится здесь и
// сохраняется в САМУ ЗАПИСЬ существующим RPC (UpdateTableRow, реквизит
// «Комментарий») — отдельного серверного контракта нет. Переход к пациенту —
// история его исследований (EcgPatientHistoryDialog, состояние страницы).
//
// Все состояния явные: загрузка, ошибка (+ «Повторить»), нет файла, файл не
// изображение, сохранение/ошибка сохранения. «Вечной загрузки» нет — каждый
// асинхронный блок снимает флаг в finally.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import {
    Activity,
    Download,
    FileImage,
    History,
    RefreshCw,
    Save,
    UserRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { FilePreviewModal } from '@/components/viewer/FilePreviewModal';
import { PrintFormButton } from '@/components/autoforms/PrintFormButton';
import { EcgStatusBadge } from '@/components/ecg/EcgStatusBadge';
import { bytesToBlob } from '@/lib/fileBytes';
import { formatValue } from '@/lib/format';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { useToastStore } from '@/store/toastStore';
import { getSDK } from '@/store/sdkStore';
import {
    CARDIO_STUDIES_TABLE,
    ECG_QUALITY_LABELS,
    loadEcgStudyDetail,
    saveEcgStudyConclusion,
    type EcgStudyDetail,
} from '@/lib/ecgStudies';
import { patientDisplayName, patientOfStudy, type EcgPatient } from '@/lib/ecgPatients';

interface Props {
    studyId: string;
    onClose: () => void;
    /** Переход к пациенту: история его исследований. */
    onOpenPatient: (patient: EcgPatient) => void;
    /** Исследование изменилось (заключение) — очередь перечитывается. */
    onSaved?: () => void;
    /**
     * Только просмотр (кабинет пациента): заключение выводится текстом,
     * запись в карточку исследования НЕДОСТУПНА (никаких изменений данных).
     */
    readOnly?: boolean;
}

interface Snapshot {
    fileName: string;
    fileSize: number;
    fileType: string;
    /** Blob URL только для изображений; иначе пусто. */
    imageUrl: string;
}

function fmtNumber(value: number | null, digits = 0): string {
    if (value === null) return '—';
    return Number.isInteger(value) ? String(value) : value.toFixed(digits);
}

function fmtProbability(value: number | null): string {
    if (value === null) return '—';
    return `${Math.round(value * 100)}%`;
}

function fmtFileSize(bytes: number): string {
    if (bytes <= 0) return '';
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

function isImageType(fileType: string, fileName: string): boolean {
    if (fileType.toLowerCase().startsWith('image/')) return true;
    return /\.(png|jpe?g|webp|gif|bmp|tiff?)$/i.test(fileName);
}

function Metric({ label, value, unit }: { label: string; value: string; unit?: string }) {
    return (
        <div className="border rounded-md px-3 py-2 bg-card">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="text-sm font-medium mt-0.5">
                {value}
                {unit && value !== '—' && <span className="text-xs text-muted-foreground ml-1">{unit}</span>}
            </div>
        </div>
    );
}

export function EcgStudyDetailDialog({
    studyId,
    onClose,
    onOpenPatient,
    onSaved,
    readOnly = false,
}: Props) {
    const showError = useToastStore((state) => state.showError);
    const showSuccess = useToastStore((state) => state.showSuccess);

    const [detail, setDetail] = useState<EcgStudyDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [reloadKey, setReloadKey] = useState(0);

    const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
    const [snapshotLoading, setSnapshotLoading] = useState(false);
    const [snapshotError, setSnapshotError] = useState('');
    const [previewOpen, setPreviewOpen] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const [conclusionDraft, setConclusionDraft] = useState('');
    const [savedConclusion, setSavedConclusion] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    const applyDetail = useCallback((loaded: EcgStudyDetail) => {
        setDetail(loaded);
        setConclusionDraft(loaded.conclusion);
        setSavedConclusion(loaded.conclusion.trim());
        setSaveError('');
    }, []);

    useEffect(() => {
        if (!studyId) {
            setDetail(null);
            setError('');
            setSnapshot(null);
            setSnapshotError('');
            setPreviewOpen(false);
            setLoading(false);
            setConclusionDraft('');
            setSavedConclusion('');
            setSaveError('');
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError('');
        setDetail(null);
        setSnapshot(null);
        setSnapshotError('');
        setPreviewOpen(false);
        setSaveError('');

        void (async () => {
            try {
                const loaded = await loadEcgStudyDetail(studyId);
                if (cancelled) return;
                applyDetail(loaded);
            } catch (loadError) {
                if (cancelled) return;
                setError(friendlyError(loadError));
                logErrorToJournal(loadError, 'ЭКГ: карточка исследования');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [studyId, reloadKey, applyDetail]);

    const fileId = detail?.fileId || '';

    // Снимок: сведения о файле + содержимое. URL освобождается при смене
    // исследования/закрытии окна (утечек Blob URL нет).
    useEffect(() => {
        setSnapshot(null);
        setSnapshotError('');
        if (!fileId) return;
        let cancelled = false;
        let objectUrl = '';
        setSnapshotLoading(true);

        void (async () => {
            try {
                const info = await getSDK().files.getFileInfo({ id: fileId });
                if (cancelled) return;
                const fileName = info.fileName || 'Снимок ЭКГ';
                const fileType = String(info.fileType || '');
                // Не изображение (PDF/архив) — содержимое не качаем: показываем
                // карточку файла и предпросмотр отдельным окном.
                if (!isImageType(fileType, fileName)) {
                    setSnapshot({
                        fileName,
                        fileSize: Number(info.fileSize || 0),
                        fileType,
                        imageUrl: '',
                    });
                    return;
                }
                const response = await getSDK().files.downloadFile({ id: fileId });
                if (cancelled) return;
                const blob = bytesToBlob(response.data, fileType || 'application/octet-stream');
                objectUrl = URL.createObjectURL(blob);
                setSnapshot({
                    fileName: response.fileName || fileName,
                    fileSize: Number(info.fileSize || 0) || blob.size,
                    fileType: fileType || blob.type,
                    imageUrl: objectUrl,
                });
            } catch (snapshotLoadError) {
                if (cancelled) return;
                setSnapshotError(friendlyError(snapshotLoadError));
                logErrorToJournal(snapshotLoadError, 'ЭКГ: снимок исследования');
            } finally {
                if (!cancelled) setSnapshotLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [fileId, reloadKey]);

    const downloadSnapshot = useCallback(async () => {
        if (!fileId) return;
        setDownloading(true);
        try {
            const response = await getSDK().files.downloadFile({ id: fileId });
            const blob = bytesToBlob(response.data, snapshot?.fileType || 'application/octet-stream');
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = response.fileName || snapshot?.fileName || 'ecg';
            link.click();
            URL.revokeObjectURL(url);
        } catch (downloadError) {
            const message = friendlyError(downloadError);
            logErrorToJournal(downloadError, 'ЭКГ: скачивание снимка');
            showError(message);
        } finally {
            setDownloading(false);
        }
    }, [fileId, snapshot, showError]);

    const saveConclusion = useCallback(async () => {
        if (!detail || readOnly) return;
        const next = conclusionDraft.trim();
        setSaving(true);
        setSaveError('');
        try {
            await saveEcgStudyConclusion(detail.id, next);
            setSavedConclusion(next);
            setDetail((prev) => (prev ? { ...prev, conclusion: next } : prev));
            onSaved?.();
            showSuccess(next ? 'Заключение сохранено' : 'Заключение очищено');
        } catch (saveConclusionError) {
            const message = friendlyError(saveConclusionError);
            logErrorToJournal(saveConclusionError, 'ЭКГ: сохранение заключения');
            setSaveError(message);
            showError(message);
        } finally {
            setSaving(false);
        }
    }, [detail, conclusionDraft, onSaved, showSuccess, showError, readOnly]);

    const patient = detail ? patientOfStudy(detail) : null;
    const patientLabel = patientDisplayName(patient);
    const createdAt = detail
        ? formatValue(detail.createdAt, 'date:dd.MM.yyyy HH:mm', 'timestamp') || detail.createdAt
        : '';
    const dirty = conclusionDraft.trim() !== savedConclusion;
    const canSave = !!detail && dirty && !saving && !readOnly;

    return (
        <UniversalWindow
            open={!!studyId}
            onClose={onClose}
            title={detail?.title ? `Исследование: ${detail.title}` : 'Исследование ЭКГ'}
            width="w-[min(980px,96vw)]"
            height="h-[min(88vh,94vh)]"
        >
            <div className="h-full overflow-auto p-4 space-y-4">
                {loading && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Загружаю исследование…
                    </div>
                )}

                {!loading && error && (
                    <div className="text-sm text-destructive border border-destructive/40 rounded-md px-3 py-2 flex items-center gap-3">
                        <span className="flex-1">{error}</span>
                        <Button size="sm" variant="outline" className="h-7" onClick={() => setReloadKey((value) => value + 1)}>
                            Повторить
                        </Button>
                    </div>
                )}

                {!loading && !error && !detail && (
                    <div className="text-sm text-muted-foreground">Исследование не открыто.</div>
                )}

                {detail && (
                    <>
                        <section className="border rounded-lg bg-card">
                            <div className="flex items-center gap-2 px-3 py-2 border-b flex-wrap">
                                <UserRound className="h-4 w-4 text-primary shrink-0" />
                                <div className="min-w-0">
                                    <div className="text-sm font-medium truncate">{patientLabel}</div>
                                    <div className="text-[11px] text-muted-foreground">
                                        {patient?.source === 'patient'
                                            ? 'Пациент из картотеки'
                                            : 'Пациент — владелец персональной записи'}
                                        {createdAt ? ` · ${createdAt}` : ''}
                                    </div>
                                </div>
                                <EcgStatusBadge code={detail.statusCode} label={detail.statusLabel} />
                                <div className="ml-auto flex flex-wrap items-center gap-2">
                                    {/* Печать протокола по записи — существующий серверный
                                        механизм (is_print-макет «Протокол разбора ЭКГ»).
                                        Кнопка скрывается, если печатных форм нет. */}
                                    <PrintFormButton
                                        objectType="catalog"
                                        objectName={CARDIO_STUDIES_TABLE}
                                        recordId={detail.id}
                                    />
                                    {!readOnly && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-7 px-2 text-[11px]"
                                            onClick={() => patient && onOpenPatient(patient)}
                                            disabled={!patient}
                                            title={patient ? 'История исследований пациента' : 'Пациент не определён'}
                                        >
                                            <History className="h-3.5 w-3.5" />
                                            История пациента
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <div className="px-3 py-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] text-muted-foreground">
                                <div>
                                    Источник
                                    <div className="text-sm text-foreground">{detail.sourceLabel || '—'}</div>
                                </div>
                                <div>
                                    Качество
                                    <div className="text-sm text-foreground">{ECG_QUALITY_LABELS[detail.qualityVerdict]}</div>
                                </div>
                                <div>
                                    Версия пайплайна
                                    <div className="text-sm text-foreground">{detail.pipelineVersion || '—'}</div>
                                </div>
                                <div>
                                    Файл
                                    <div className="text-sm text-foreground">{detail.fileId ? 'прикреплён' : 'нет'}</div>
                                </div>
                            </div>
                        </section>

                        <section className="border rounded-lg bg-card p-3 space-y-2">
                            <div className="flex items-center gap-2">
                                <Activity className="h-4 w-4 text-primary" />
                                <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
                                    Снимок ЭКГ
                                </h3>
                            </div>
                            {!detail.fileId && (
                                <div className="text-sm text-muted-foreground">Снимок не прикреплён.</div>
                            )}
                            {detail.fileId && snapshotLoading && (
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                                    Загружаю снимок…
                                </div>
                            )}
                            {detail.fileId && !snapshotLoading && snapshotError && (
                                <div className="text-xs text-destructive border border-destructive/40 rounded-md px-2 py-1.5">
                                    {snapshotError}
                                </div>
                            )}
                            {detail.fileId && !snapshotLoading && !snapshotError && snapshot && (
                                <div className="space-y-2">
                                    {snapshot.imageUrl ? (
                                        <button
                                            type="button"
                                            className="block rounded-md border overflow-hidden bg-muted/30 max-w-full"
                                            onClick={() => setPreviewOpen(true)}
                                            title="Открыть снимок полностью"
                                        >
                                            <img
                                                src={snapshot.imageUrl}
                                                alt={`Снимок ЭКГ: ${snapshot.fileName}`}
                                                className="max-h-[320px] w-auto max-w-full object-contain"
                                            />
                                        </button>
                                    ) : (
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                            <FileImage className="h-4 w-4" />
                                            Предпросмотр доступен отдельным окном.
                                        </div>
                                    )}
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-sm flex-1 min-w-0 truncate" title={snapshot.fileName}>
                                            {snapshot.fileName}
                                        </span>
                                        {snapshot.fileSize > 0 && (
                                            <span className="text-xs text-muted-foreground">
                                                {fmtFileSize(snapshot.fileSize)}
                                            </span>
                                        )}
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-7"
                                            onClick={() => setPreviewOpen(true)}
                                        >
                                            Открыть снимок
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-7"
                                            onClick={() => void downloadSnapshot()}
                                            disabled={downloading}
                                        >
                                            <Download className="h-3.5 w-3.5" />
                                            {downloading ? 'Скачиваю…' : 'Скачать'}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </section>

                        <section>
                            <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Результат анализа</h3>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                <Metric label="ЧСС" value={fmtNumber(detail.measurements.hrBpm)} unit="уд/мин" />
                                <Metric label="PR" value={fmtNumber(detail.measurements.prMs)} unit="мс" />
                                <Metric label="QRS" value={fmtNumber(detail.measurements.qrsMs)} unit="мс" />
                                <Metric label="QT" value={fmtNumber(detail.measurements.qtMs)} unit="мс" />
                                <Metric label="QTc" value={fmtNumber(detail.measurements.qtcMs)} unit="мс" />
                                <Metric label="Электрическая ось" value={fmtNumber(detail.measurements.axisDeg)} unit="°" />
                            </div>
                        </section>

                        <section>
                            <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                                Вероятностные находки
                            </h3>
                            {detail.findings.length === 0 ? (
                                <div className="text-sm text-muted-foreground">
                                    Находок нет — вероятности не превысили порог обнаружения.
                                </div>
                            ) : (
                                <ul className="border rounded-lg divide-y bg-card">
                                    {detail.findings.map((finding, index) => (
                                        <li
                                            key={`${finding.code}-${index}`}
                                            className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
                                        >
                                            <span>{finding.code}</span>
                                            <span className="text-xs text-muted-foreground">
                                                {fmtProbability(finding.probability)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>

                        <section className="border rounded-lg bg-card p-3 space-y-2">
                            <div className="flex items-center gap-2">
                                <h3 className="text-xs uppercase tracking-wide text-muted-foreground flex-1">
                                    Заключение врача
                                </h3>
                                <span className="text-[11px] text-muted-foreground">
                                    {readOnly
                                        ? savedConclusion ? 'заполнено' : 'не заполнено'
                                        : dirty ? 'не сохранено' : savedConclusion ? 'сохранено' : 'не заполнено'}
                                </span>
                            </div>
                            {readOnly ? (
                                <div className="whitespace-pre-wrap break-words text-sm">
                                    {savedConclusion || 'Заключение пока не заполнено.'}
                                </div>
                            ) : (
                                <>
                                    <textarea
                                        value={conclusionDraft}
                                        onChange={(event) => setConclusionDraft(event.target.value)}
                                        placeholder="Опишите заключение по снимку ЭКГ: ритм, ЧСС, изменения, рекомендации…"
                                        className="w-full min-h-[120px] rounded-md border bg-background px-2 py-1.5 text-sm resize-y"
                                        aria-label="Заключение врача"
                                    />
                                    {saveError && (
                                        <div className="text-xs text-destructive border border-destructive/40 rounded-md px-2 py-1.5">
                                            {saveError}
                                        </div>
                                    )}
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Button
                                            size="sm"
                                            className="h-8"
                                            onClick={() => void saveConclusion()}
                                            disabled={!canSave}
                                        >
                                            {saving
                                                ? <RefreshCw className="h-4 w-4 animate-spin" />
                                                : <Save className="h-4 w-4" />}
                                            {saving ? 'Сохраняю…' : 'Сохранить заключение'}
                                        </Button>
                                        {dirty && !saving && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-8 text-[11px]"
                                                onClick={() => setConclusionDraft(detail.conclusion)}
                                                disabled={conclusionDraft === detail.conclusion}
                                            >
                                                Отменить изменения
                                            </Button>
                                        )}
                                        <span className="text-[11px] text-muted-foreground ml-auto">
                                            Заключение сохраняется в карточку исследования
                                        </span>
                                    </div>
                                </>
                            )}
                        </section>
                    </>
                )}
            </div>

            {previewOpen && detail?.fileId && (
                <FilePreviewModal
                    fileId={detail.fileId}
                    fileName={snapshot?.fileName || 'Снимок ЭКГ'}
                    onClose={() => setPreviewOpen(false)}
                />
            )}
        </UniversalWindow>
    );
}
